import { ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Tone from "tone";
import { Save, Upload } from "lucide-react";
import Button from "~/components/ui/Button";
import ProjectImportModal from "~/components/ProjectImportModal";
import { useMusicStore } from "~/stores/useMusicStore";

export default function Test(): ReactElement {
  const navigate = useNavigate();
  const exportProject = useMusicStore((state) => state.exportProject);
  const importProject = useMusicStore((state) => state.importProject);

  const [isPlaying, setIsPlaying] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [showMuteWarning, setShowMuteWarning] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exportedJson, setExportedJson] = useState<string | null>(null);

  const addLog = (message: string) => {
    console.log(`[TEST AUDIO] ${message}`);
    setLogs(prev => [...prev, `${new Date().toISOString().split('T')[1].split('.')[0]} - ${message}`]);
  };

  // Detect mute switch by checking if audio is actually playing
  const checkMuteSwitch = async (ctx: AudioContext): Promise<boolean> => {
    try {
      addLog("🔇 [MUTE CHECK] Checking if device mute switch is on...");

      // Create a very short silent buffer to test audio output
      const testBuffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = testBuffer;

      // Check if audio hardware is actually outputting
      // On iOS with mute switch ON: context is "running" but hardware is muted

      // Check baseLatency - when mute switch is on, this is often 0
      const baseLatency = (ctx as any).baseLatency || 0;
      const outputLatency = (ctx as any).outputLatency || 0;

      addLog(`🔇 [MUTE CHECK] Base latency: ${baseLatency}, Output latency: ${outputLatency}`);

      // If both latencies are 0, likely muted (not 100% reliable but best we can do)
      if (baseLatency === 0 && outputLatency === 0 && ctx.state === 'running') {
        addLog("⚠️ [MUTE CHECK] Device may be muted (both latencies are 0)");
        return true;
      }

      addLog("✅ [MUTE CHECK] Device appears to be unmuted");
      return false;
    } catch (error) {
      addLog(`❌ [MUTE CHECK] Error checking mute: ${error}`);
      return false;
    }
  };

  const testAudioAsync = async () => {
    try {
      addLog("🎵 [iOS 18.3.1 FIX] Test Audio button clicked");

      if (isPlaying) {
        addLog("⏸️ [iOS 18.3.1 FIX] Audio already playing, stopping...");
        setIsPlaying(false);
        return;
      }

      setIsPlaying(true);

      // Log audio context state BEFORE
      addLog(`🔊 [iOS 18.3.1 FIX] Audio context state BEFORE resume: ${Tone.getContext().state}`);
      addLog(`🔊 [iOS 18.3.1 FIX] Audio context currentTime: ${Tone.getContext().currentTime}`);

      // Call resume() synchronously in user gesture, THEN await
      const ctx = Tone.getContext();
      if (ctx.state === 'suspended') {
        addLog("🔊 [FIX] Calling resume() synchronously in user gesture...");
        const resumePromise = ctx.resume(); // Call synchronously, store promise
        addLog("🔊 [FIX] Resume promise created, now awaiting...");
        await resumePromise; // Then await the promise
        addLog("🔊 [FIX] Resume promise resolved");
      }

      // Log audio context state AFTER
      addLog(`✅ [FIX] Resume completed`);
      addLog(`🔊 [FIX] Audio context state AFTER resume: ${Tone.getContext().state}`);
      addLog(`🔊 [iOS 18.3.1 FIX] Audio context sampleRate: ${Tone.getContext().sampleRate}`);
      addLog(`🔊 [iOS 18.3.1 FIX] Audio context destination: ${Tone.getDestination()}`);

      // Check for mute switch
      // Get the underlying native AudioContext from Tone.js context
      const nativeCtx = ctx.rawContext as AudioContext;
      const isMuted = await checkMuteSwitch(nativeCtx);
      if (isMuted) {
        setShowMuteWarning(true);
        addLog("⚠️ [WARNING] Device may be muted - check mute switch on side of device");
      } else {
        setShowMuteWarning(false);
      }

      // Create a simple oscillator
      addLog("🎹 [iOS 18.3.1 FIX] Creating oscillator (440Hz sine wave)...");
      const osc = new Tone.Oscillator(440, "sine").toDestination();
      addLog(`✅ [iOS 18.3.1 FIX] Oscillator created, volume: ${osc.volume.value}dB`);

      // Start the oscillator
      addLog("▶️ [iOS 18.3.1 FIX] Starting oscillator...");
      osc.start();
      addLog("✅ [iOS 18.3.1 FIX] Oscillator started");

      // Play for 1 second
      addLog("⏱️ [iOS 18.3.1 FIX] Playing for 1 second...");
      setTimeout(() => {
        addLog("⏹️ [iOS 18.3.1 FIX] Stopping oscillator...");
        osc.stop();
        osc.dispose();
        addLog("✅ [iOS 18.3.1 FIX] Oscillator stopped and disposed");
        setIsPlaying(false);
      }, 1000);

    } catch (error) {
      addLog(`❌ [iOS 18.3.1 FIX] Error: ${error}`);
      console.error("[TEST AUDIO] [iOS 18.3.1 FIX] Error:", error);
      setIsPlaying(false);
    }
  };

  const handleSaveProject = () => {
    try {
      const json = exportProject();
      setExportedJson(json);
      console.log("📂 [PROJECT EXPORT] Project saved:");
      console.log(json);
      addLog("✅ Project exported - check console and textarea below");
    } catch (error) {
      addLog(`❌ Failed to export project: ${error}`);
      console.error("[PROJECT EXPORT] Error:", error);
    }
  };

  const handleLoadProject = (json: string) => {
    try {
      importProject(json);
      addLog("✅ Project imported successfully - redirecting to home...");
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (error) {
      addLog(`❌ Failed to import project: ${error}`);
      throw error; // Re-throw to show in modal
    }
  };

  const testAudioSync = () => {
    try {
      addLog("🎵 [SYNC] Test Audio button clicked");

      if (isPlaying) {
        addLog("⏸️ [SYNC] Audio already playing, stopping...");
        setIsPlaying(false);
        return;
      }

      setIsPlaying(true);

      // Log audio context state BEFORE
      addLog(`🔊 [SYNC] Audio context state BEFORE resume: ${Tone.getContext().state}`);
      addLog(`🔊 [SYNC] Audio context currentTime: ${Tone.getContext().currentTime}`);

      // Resume context synchronously (no await)
      const ctx = Tone.getContext();
      if (ctx.state === 'suspended') {
        addLog("🔊 [SYNC] Resuming audio context synchronously...");
        ctx.resume(); // NO AWAIT - completely synchronous
      }

      // Log audio context state AFTER
      addLog(`✅ [SYNC] Context resume called (synchronously)`);
      addLog(`🔊 [SYNC] Audio context state AFTER resume: ${Tone.getContext().state}`);
      addLog(`🔊 [SYNC] Audio context sampleRate: ${Tone.getContext().sampleRate}`);

      // Create and start oscillator immediately (no await anywhere)
      addLog("🎹 [SYNC] Creating oscillator (440Hz sine wave) synchronously...");
      const osc = new Tone.Oscillator(440, "sine").toDestination();
      addLog(`✅ [SYNC] Oscillator created, volume: ${osc.volume.value}dB`);

      // Start immediately
      addLog("▶️ [SYNC] Starting oscillator synchronously...");
      osc.start();
      addLog("✅ [SYNC] Oscillator started");

      // Play for 1 second
      addLog("⏱️ [SYNC] Playing for 1 second...");
      setTimeout(() => {
        addLog("⏹️ [SYNC] Stopping oscillator...");
        osc.stop();
        osc.dispose();
        addLog("✅ [SYNC] Oscillator stopped and disposed");
        setIsPlaying(false);
      }, 1000);

    } catch (error) {
      addLog(`❌ [SYNC] Error: ${error}`);
      console.error("[TEST AUDIO] [SYNC] Error:", error);
      setIsPlaying(false);
    }
  };

  return (
    <main className="flex flex-1 px-4 md:px-6 py-4 md:py-6 pb-12">
      <div className="flex flex-col items-center w-full h-full">
        <div className="max-w-2xl w-full space-y-4 md:space-y-6">
          <div className="text-center space-y-2 md:space-y-4 mb-4 md:mb-6 mt-2 md:mt-12">
            <h1 className="text-2xl md:text-4xl font-bold text-purple-400">Test & Debug</h1>
            <p className="text-sm md:text-base text-gray-400">
              Audio testing and project save/load utilities
            </p>
          </div>

          {/* Project Save/Load Section */}
          <div className="bg-gray-900 border-2 border-purple-700 rounded-lg p-4 md:p-6 space-y-3 md:space-y-4">
            <h2 className="text-lg md:text-xl font-bold text-purple-400">Project Save/Load</h2>
            <p className="text-xs md:text-sm text-gray-400">
              Save your current device configuration (patterns, CCs, panel states) or load a previously saved project.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleSaveProject}
                icon={<Save size={18} />}
                className="flex-1"
              >
                Save Project
              </Button>
              <Button
                onClick={() => setShowImportModal(true)}
                icon={<Upload size={18} />}
                variant="secondary"
                className="flex-1"
              >
                Load Project
              </Button>
            </div>

            {exportedJson && (
              <div className="space-y-2">
                <label className="text-xs md:text-sm font-semibold text-gray-400">
                  Exported Project JSON (copy this to save):
                </label>
                <textarea
                  value={exportedJson}
                  readOnly
                  className="w-full h-40 md:h-48 p-3 md:p-4 bg-gray-950 border-2 border-gray-800 rounded font-mono text-xs text-gray-300 resize-none"
                  onClick={(e) => e.currentTarget.select()}
                />
                <p className="text-xs text-gray-500">
                  Click to select all, then copy this JSON to save your project.
                </p>
              </div>
            )}
          </div>

          {/* Audio Test Section */}
          <div className="bg-gray-900 border-2 border-gray-700 rounded-lg p-4 md:p-6 space-y-3 md:space-y-4">
            <h2 className="text-lg md:text-xl font-bold text-gray-400">Audio Test</h2>
            <p className="text-xs md:text-sm text-gray-400">
              Test Tone.js audio output with a simple 440Hz sine wave
            </p>

          {showMuteWarning && (
            <div className="bg-red-900/30 border-2 border-red-500 rounded-lg p-4 md:p-6 space-y-2 md:space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl md:text-4xl">🔇</span>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-red-400">Device May Be Muted</h3>
                  <p className="text-sm md:text-base text-red-300">Check the mute switch on the side of your device</p>
                </div>
              </div>
              <div className="text-xs md:text-sm text-red-200 space-y-2">
                <p>The audio context is running, but your device may be muted.</p>
                <p className="font-semibold">To fix this:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Check the physical mute switch on the side of your device</li>
                  <li>Make sure it's NOT showing orange</li>
                  <li>Turn up the volume</li>
                  <li>Try the test button again</li>
                </ol>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 md:gap-4 items-center">
            <Button
              onClick={testAudioAsync}
              disabled={isPlaying}
              className="px-6 md:px-8 py-3 md:py-4 text-base md:text-lg w-full sm:w-64"
            >
              {isPlaying ? "Playing..." : "Test Audio (iOS 18.3.1 Fix)"}
            </Button>

            <Button
              onClick={testAudioSync}
              disabled={isPlaying}
              className="px-6 md:px-8 py-3 md:py-4 text-base md:text-lg w-full sm:w-64"
              variant="secondary"
            >
              {isPlaying ? "Playing..." : "Test Audio (Sync - Original)"}
            </Button>
          </div>

          {logs.length > 0 && (
            <div className="bg-gray-900 rounded-lg p-3 md:p-4 border border-gray-800">
              <h2 className="text-xs md:text-sm font-semibold text-gray-400 mb-2">Diagnostic Logs:</h2>
              <div className="space-y-1 max-h-64 md:max-h-96 overflow-y-auto font-mono text-xs">
                {logs.map((log, i) => (
                  <div key={i} className="text-gray-300">
                    {log}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs md:text-sm text-gray-500 space-y-2 border-t border-gray-800 pt-3 md:pt-4">
            <p className="font-semibold">iOS 18.3.1 Audio Bug Fix:</p>
            <div className="space-y-2 md:space-y-3 text-gray-400">
              <div>
                <p className="font-semibold text-purple-400">iOS 18.3.1 Fix Button:</p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-xs md:text-sm">
                  <li>Calls <code className="text-xs">context.resume()</code> SYNCHRONOUSLY (no await)</li>
                  <li>MUST be called directly in user gesture for iOS 18.3.1+</li>
                  <li>Any await before resume() breaks audio activation</li>
                  <li>This is the fix applied to the main app Play button</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-green-400">Sync Test (Original):</p>
                <ul className="list-disc list-inside space-y-1 ml-2 text-xs md:text-sm">
                  <li>Same synchronous pattern from previous testing</li>
                  <li>Should also work with the fix</li>
                </ul>
              </div>
            </div>
            <p className="mt-3 md:mt-4 text-xs md:text-sm text-gray-400 bg-yellow-900/20 p-3 rounded border border-yellow-700/50">
              <strong>iOS 18.3.1 Breaking Change:</strong> Apple introduced a new restriction
              where Web Audio API must be activated fully synchronously within the user
              gesture event handler. Any async operations (await) before calling resume()
              will prevent audio from working.
            </p>
          </div>
          </div>
          {/* End Audio Test Section */}
        </div>
      </div>

      {/* Project Import Modal */}
      <ProjectImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleLoadProject}
      />
    </main>
  );
}
